/**
 * Global Context Bar
 *
 * Persistent selector for Client > Matter > Project hierarchy.
 * Retains selections as users navigate between pages.
 */

import React from 'react';
import { Flex, useTheme, spacing } from '@fti/design-system';
import { useHierarchy } from '../../context/HierarchyContext';
import { useClients } from '../../api/clients';
import { useMatters } from '../../api/matters';
import { useProjects } from '../../api/projects';

export function GlobalContextBar() {
  const { theme } = useTheme();
  const {
    selectedClient,
    selectedMatter,
    selectedProjectName,
    selectedProject,
    selectClient,
    selectMatter,
    selectProjectName,
    selectProject,
    dataVersion,
  } = useHierarchy();

  const { data: clients } = useClients({ limit: 100, refreshKey: dataVersion });
  const { data: matters } = useMatters(
    selectedClient
      ? { underClientId: selectedClient.id, limit: 100, refreshKey: dataVersion }
      : undefined
  );
  const { data: projects } = useProjects(
    selectedMatter
      ? { underMatterId: selectedMatter.id, limit: 1000, refreshKey: dataVersion }
      : undefined
  );

  // Unique project names under the selected matter (a project has many versions).
  const projectNames = React.useMemo(() => {
    const names = new Set<string>();
    projects?.forEach((p) => names.add(p.name));
    return Array.from(names).sort();
  }, [projects]);

  // A project *name* can span several version rows, each with its own
  // id_project. Show the id when the name resolves to exactly one row;
  // otherwise say how many versions it spans — the individual ids are then
  // on the Version dropdown.
  const projectLabel = React.useCallback((name: string) => {
    const rows = (projects ?? []).filter((p) => p.name === name);
    if (rows.length === 1) return `${name} · #${rows[0].id}`;
    if (rows.length > 1) return `${name} · ${rows.length} versions`;
    return name;
  }, [projects]);

  // Versions for the selected project name, newest first.
  const versions = React.useMemo(() => {
    if (!selectedProjectName) return [];
    return (projects ?? [])
      .filter((p) => p.name === selectedProjectName)
      .sort((a, b) => String(b.version).localeCompare(String(a.version)));
  }, [projects, selectedProjectName]);

  const borderColor = theme.colors.border.default;
  const bgColor = theme.colors.background.paper;
  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  const selectStyle: React.CSSProperties = {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: '4px',
    border: `1px solid ${borderColor}`,
    background: bgColor,
    color: textColor,
    fontSize: '0.825rem',
    minWidth: '160px',
    // Wide enough that the appended · #id stays visible instead of being the
    // first thing truncated away.
    maxWidth: '280px',
  };

  const handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    const client = clients?.find((c) => c.id === id) || null;
    selectClient(client);
  };

  const handleMatterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    const matter = matters?.find((m) => m.id === id) || null;
    selectMatter(matter);
  };

  const handleProjectNameChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    selectProjectName(e.target.value || null);
  };

  const handleVersionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    const project = versions.find((p) => p.id === id) || null;
    selectProject(project);
  };

  return (
    <div style={{
      padding: `${spacing.sm} ${spacing.lg}`,
      borderBottom: `1px solid ${borderColor}`,
      background: bgColor,
    }}>
      <Flex align="center" gap="md">
        <span style={{ fontSize: '0.75rem', color: mutedColor, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Context
        </span>

        <select style={selectStyle} value={selectedClient?.id || ''} onChange={handleClientChange}>
          <option value="">Select Client...</option>
          {/* Ensure the current selection always renders, even if the fetched
              list is stale (e.g. a client added since this bar last loaded). */}
          {selectedClient && !clients?.some((c) => c.id === selectedClient.id) && (
            <option value={selectedClient.id}>{selectedClient.name} · #{selectedClient.id}</option>
          )}
          {clients?.map((c) => (
            <option key={c.id} value={c.id}>{c.name} · #{c.id}</option>
          ))}
        </select>

        <span style={{ color: mutedColor }}>/</span>

        <select
          style={selectStyle}
          value={selectedMatter?.id || ''}
          onChange={handleMatterChange}
          disabled={!selectedClient}
        >
          <option value="">Select Matter...</option>
          {selectedMatter && !matters?.some((m) => m.id === selectedMatter.id) && (
            <option value={selectedMatter.id}>{selectedMatter.name} · #{selectedMatter.id}</option>
          )}
          {matters?.map((m) => (
            <option key={m.id} value={m.id}>{m.name} · #{m.id}</option>
          ))}
        </select>

        <span style={{ color: mutedColor }}>/</span>

        <select
          style={selectStyle}
          value={selectedProjectName || ''}
          onChange={handleProjectNameChange}
          disabled={!selectedMatter}
        >
          <option value="">Select Project...</option>
          {selectedProjectName && !projectNames.includes(selectedProjectName) && (
            <option value={selectedProjectName}>{selectedProjectName}</option>
          )}
          {projectNames.map((name) => (
            <option key={name} value={name}>{projectLabel(name)}</option>
          ))}
        </select>

        <span style={{ color: mutedColor }}>/</span>

        <select
          style={selectStyle}
          value={selectedProject?.id || ''}
          onChange={handleVersionChange}
          disabled={!selectedProjectName}
        >
          <option value="">Select Version...</option>
          {selectedProject && !versions.some((p) => p.id === selectedProject.id) && (
            <option value={selectedProject.id}>v{selectedProject.version} · #{selectedProject.id}</option>
          )}
          {versions.map((p) => (
            <option key={p.id} value={p.id}>v{p.version} · #{p.id}</option>
          ))}
        </select>
      </Flex>
    </div>
  );
}
